<template>
	<section class="section-base form-section-base">
		<div class="form-section-base__block-outer">
			<div class="form-section-base__block-inner">
				<div class="mb-5">
					<div class="form-section-base__title">Опись документов</div>
					<div v-if="Boolean(subtitle)" class="form-section-base__sub-title">
						{{ subtitle }}
					</div>
				</div>
				<AttachDocumentMultiple
					v-model="$v.documents.$model"
					:error-messages="getValidationErrorText($v.documents)"
					:max-length="255"
					:can-load-from-profile="true"
          :withSelect="statementType === StatementType.NOTICE_RECOVERY && isNoticeRecoveryClassifierEnable"
					:document-types="documentTypes"
				/>
			</div>
		</div>
		<v-container>
			<Footer
				@prev-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.PREV,
              step: StepName.DOCUMENTS,
            })
        "
				@next-btn-click="
          () =>
            leaveStep({
              stepThis: this,
              direction: StepDirection.NEXT,
              step: StepName.DOCUMENTS,
            })
        "
				:loading-next="loadingNext"
			/>
		</v-container>
	</section>
</template>

<script>
import cloneDeep from 'clone-deep';
import {requiredIf} from 'vuelidate/lib/validators';

import {ServicesModule} from '@/store/modules/services';
import AttachDocumentMultiple from '@/components/base/AttachDocument/AttachDocumentMultiple';
import Footer from '@/components/services/elements/Footer';
import {DOCS_REQUIREMENTS, getValidationErrorText, DOCS_GROUPS_REQUIRED} from '@/utils/validate';
import {checkInvalid, leaveStep} from '@/utils/services';
import {StepDirection, StatementType, StepName} from '@/types/services';
import {MachineReadableFillType} from '@/constants';
import {UploadDocumentTypes} from "@/utils/uploadDocumentTypes";
import { isNoticeRecoveryClassifierEnable } from '@/app.config';

const Subtitle = {
	[StatementType.NOTICE_RECOVERY]:
		'К заявлению в обязательном порядке прилагается документ, подтверждающий передачу (перечисление) заемщику денежных средств или передачу ему других вещей по нотариально удостоверенному договору займа.',
};

// Массив заявлений, у которых может не быть ни одного документа, снята обязательность
const statementsNotRequired = [
	StatementType.SECURING_EVIDENCE,
	StatementType.STORAGE_ISSUE,
	StatementType.EXTRACTION_ROD,
	StatementType.ROD,
	StatementType.EGRN_CONFIRM,
];

export default {
	name: 'StepDocuments',
	components: {
		AttachDocumentMultiple,
		Footer,
	},
	props: {
		loadingNext: {
			type: Boolean,
			required: true,
		},
		statementType: {
			type: String,
			required: true,
		},
		isAlternativeServiceEdit: {
			type: Boolean,
			required: true,
		},
	},
	data: () => ({
		StepDirection,
		StepName,
		leaveStep,
		getValidationErrorText,
		invalidFields: [],
		documents: [],
		StatementType,
    isNoticeRecoveryClassifierEnable,
	}),
	validations() {
    const documents = {
      DOCUMENTS_MIN_LENGTH: requiredIf(
        () =>
          !(
            statementsNotRequired.includes(this.statementType) ||
            this.isAlternativeServiceEdit
          )
      ),
    }
    if (!this.isAlternativeServiceEdit) {
      documents.DOCS_REQUIREMENTS = DOCS_REQUIREMENTS(this.documents)
      documents.DOCS_GROUPS_REQUIRED = DOCS_GROUPS_REQUIRED(
        this.documents,
        this.statementType == StatementType.NOTICE_RECOVERY ? ['10000', '11200'] : []
      )
    }
		return {
			documents
		};
	},
	computed: {
		subtitle() {
			return Subtitle[this.statementType];
		},
		isFullViewerMode() {
			return (
				this.statementType === 'STORAGE_ACCEPT' ||
				this.statementType === 'STORAGE_ISSUE' ||
				this.statementType === 'DOCUMENT_CONVERSION'
			);
		},
		documentTypes() {
			const excludedDocs = ['12104', '12302']
			const docs = UploadDocumentTypes.filter((item) => ['12100', '12200', '12300', '12400', '12500'].includes(item.groupCode))
			return docs.map(item => {
				return {
					...item,
					documents: item.documents.filter(doc => !excludedDocs.includes(doc.value))
				}
			})
		}
	},
	created() {
		this.prefillDraft();
	},
	methods: {
		touchFields() {
			this.invalidFields = [];
			this.$v.$touch();
			checkInvalid(this.$v, this.invalidFields);
		},

		assemblyModel() {
			return cloneDeep(
				this.documents.filter((document) => !document.preventDelete)
			);
		},

		getApplicantStatusDocument() {
			if (
				!ServicesModule.statement.subjects.applicant?.trustee?.documents
					?.statusDocument?.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.applicant.trustee.documents
					.statusDocument,
			];
		},

		getApplicantAuthorityDocument() {
			if (
				!ServicesModule.statement.subjects.applicant?.trustee?.documents
					?.authorityDocument?.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.applicant.trustee.documents
					.authorityDocument,
			];
		},

		getApplicantTrusteeOrganizationAuthorityDocument() {
			if (
				!ServicesModule.statement.subjects.applicant?.trusteeOrganization
					?.documents?.authorityDocument?.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.applicant.trusteeOrganization
					.documents.authorityDocument,
			];
		},

		getProxyMrpa() {
			const proxies = ServicesModule.statement.rodDetails?.proxies;

			if (!proxies || proxies.length === 0) return [];

			const mrpaProxies = proxies.filter(
				(proxy) =>
					proxy.machineReadable &&
					proxy.machineReadableFillType === MachineReadableFillType.BY_FILE &&
					proxy.machineReadableFile
			);

			if (mrpaProxies.length === 0) return [];

			return mrpaProxies.map((proxy) => ({
				file: proxy.machineReadableFile,
				fileSig: proxy.machineReadableSig,
			}));
		},

		getApplicantRightholderAuthorityDocument() {
			if (
				!ServicesModule.statement.subjects.applicantRightholder?.trustee
					?.documents?.authorityDocument?.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.applicantRightholder.trustee.documents
					.authorityDocument,
			];
		},

		getDebtorStatusDocument() {
			if (
				!ServicesModule.statement.subjects.debtor?.trustee?.documents
					.statusDocument?.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.debtor.trustee.documents
					.statusDocument,
			];
		},

		getDebtorAuthorityDocument() {
			if (
				!ServicesModule.statement.subjects.debtor?.trustee?.documents
					.authorityDocument.file
			) {
				return [];
			}

			return [
				ServicesModule.statement.subjects.debtor.trustee.documents
					.authorityDocument,
			];
		},

		getCreditorsDocuments() {
			if (!ServicesModule.statement.subjects.creditors) {
				return [];
			}

			return ServicesModule.statement.subjects.creditors
				.map((creditor) => {
					if (!creditor.trustee || !creditor.trustee.documents) return [];

					const creditorDocuments = [];

					for (const document of Object.values(creditor.trustee.documents)) {
						if (document && document.file) creditorDocuments.push(document);
					}

					return creditorDocuments;
				})
				.flat();
		},

		getDebtorsDocuments() {
			if (!ServicesModule.statement.subjects.debtors) {
				return [];
			}

			return ServicesModule.statement.subjects.debtors
				.map((debtor) => {
					if (!debtor.trustee || !debtor.trustee.documents) return [];

					const debtorDocuments = [];

					for (const document of Object.values(debtor.trustee.documents)) {
						if (document && document.file) debtorDocuments.push(document);
					}

					return debtorDocuments;
				})
				.flat();
		},

		getObligationDocument() {
			if (!ServicesModule.statement.obligationDocument?.file) {
				return [];
			}

			return [ServicesModule.statement.obligationDocument];
		},

		getTranslatedDocument() {
			if (!ServicesModule.statement.translatedDocument) {
				return [];
			}

			return [ServicesModule.statement.translatedDocument];
		},

		getConversionDocument() {
			if (!ServicesModule.statement.conversionDocument) {
				return [];
			}

			return [ServicesModule.statement.conversionDocument];
		},

		getDocuments(documents) {
			return documents || [];
		},

		// получаем объект (один документ), добавляем его в массив
		getDocument(document) {
			return document ? [document] : [];
		},

		addPreventDelete(documents) {
			return documents.map((document) => ({
				...document,
				preventDelete: true,
			}));
		},

		prefillDraft() {
			this.documents = [
				...this.addPreventDelete(this.getApplicantStatusDocument()),
				...this.addPreventDelete(this.getApplicantAuthorityDocument()),
				...this.addPreventDelete(
					this.getApplicantRightholderAuthorityDocument()
				),
				...this.addPreventDelete(
					this.getApplicantTrusteeOrganizationAuthorityDocument()
				),
				...this.addPreventDelete(this.getProxyMrpa()),
				...this.addPreventDelete(this.getDebtorStatusDocument()),
				...this.addPreventDelete(this.getDebtorAuthorityDocument()),
				...this.addPreventDelete(this.getDebtorsDocuments()),
				...this.addPreventDelete(this.getCreditorsDocuments()),
				...this.addPreventDelete(this.getObligationDocument()),
				...this.addPreventDelete(this.getTranslatedDocument()),
				...this.addPreventDelete(this.getConversionDocument()),
				...this.addPreventDelete(
					this.getDocuments(ServicesModule.statement.prevCreditorDocuments)
				),
				...this.addPreventDelete(
					this.getDocuments(ServicesModule.statement.prevDebtorDocuments)
				),
				...this.addPreventDelete(
					this.getDocuments(ServicesModule.statement.notarialDocuments)
				),
				...this.addPreventDelete(
					this.getDocuments(ServicesModule.statement.statementParamDocuments)
				),
				...this.addPreventDelete(
					this.getDocuments(ServicesModule.statement.transferDocuments)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.storageDocument)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.contractDoc)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.preliminaryContractDoc)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.protectionPrivatizationDoc)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.compensationDamageDoc)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.lawsuitRightHolderDoc)
				),
				...this.addPreventDelete(
					this.getDocument(
						ServicesModule.statement.foreclosureEstateRightholderDoc
					)
				),
				...this.addPreventDelete(
					this.getDocument(ServicesModule.statement.otherRightholder)
				),
				...this.getDocuments(ServicesModule.statement.documents),
			];
		},
	},
};
</script>
